import React from 'react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import sprite from '../assets/img/svg/svg-sprite.svg'
import { isRobotYandexDeliveryType } from '../robotDelivery'
import { useAppSelector } from '../store'
import { useGetOrdersQuery } from '../store/api/api'
import { getDateString, getTimeString, parseEatPlace, translateToStyleCorner, translateToStyleOrder } from '../utils'

import Loader from './Loader'
import MarketSelect from './MarketSelect'
import OutletsList from './OutletsList'
import Profile from './Profile'

/** Inline SVG (структура как в `assets/img/svg/robot.svg`), цвета по слоям. */
const RobotDeliveryIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    width={18}
    height={18}
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    role="img"
    aria-label="Доставка роботом Яндекса"
  >
    <title>Доставка роботом Яндекса</title>
    <path
      d="M3 14C4.65685 14 6 15.3431 6 17C6 18.6569 4.65685 20 3 20C1.34315 20 2.06162e-06 18.6569 0 17C0 15.3431 1.34315 14 3 14ZM10 14C11.6569 14 13 15.3431 13 17C13 18.6569 11.6569 20 10 20C8.34315 20 7 18.6569 7 17C7 15.3431 8.34315 14 10 14ZM17 14C18.6569 14 20 15.3431 20 17C20 18.6569 18.6569 20 17 20C15.3431 20 14 18.6569 14 17C14 15.3431 15.3431 14 17 14Z"
      fill="#000000"
    />
    <path
      d="M14 2L17.7275 2.93164C19.063 3.26552 20 4.46617 20 5.84277V8C20 9.65179 19.9999 10.7077 19.6836 11.3408C19.3932 11.922 18.922 12.3932 18.3408 12.6836C17.7077 12.9999 16.8819 13 15.2305 13H4.76953C3.11796 13 1.97587 12.9999 1.34277 12.6836C0.761633 12.3932 9.68371e-07 11.5 0 10V6H7.43848C8.35612 5.99999 9.15625 5.37556 9.37891 4.48535L10 2L10.3105 0.757812C10.4218 0.312645 10.8224 0 11.2812 0H14V2ZM13.25 5.25C12.8359 5.25004 12.5 5.58586 12.5 6V9.5C12.5 9.91414 12.8359 10.25 13.25 10.25C13.6642 10.25 14 9.91417 14 9.5V6C14 5.58583 13.6642 5.25 13.25 5.25ZM17.251 5.25C16.8369 5.25 16.5011 5.58596 16.501 6L16.5 9.5C16.4999 9.91414 16.8359 10.25 17.25 10.25C17.664 10.2499 17.9999 9.914 18 9.5V6C18.0001 5.58601 17.6649 5.25025 17.251 5.25Z"
      fill="#DC2626"
    />
    <path
      d="M10 2H4.77C3.11814 2 1.97585 2 1.3427 2.31637C0.761556 2.60675 6.07975e-06 3.48126 0 5V6H7.43844C8.35618 6 9.15615 5.37541 9.37873 4.48507L10 2Z"
      fill="#9CA3AF"
    />
  </svg>
)

const Market: React.FC = () => {
  // --- EXTERNAL STATE --- //

  const navigate = useNavigate()

  const market = useAppSelector((state) => state.market)

  const selectedMarket = market.selected ? market.list.find((item) => item.id === market.selected) ?? null : null

  const { data: ordersData, isLoading, isError } = useGetOrdersQuery(market.selected ?? 0, {
    skip: market.selected == null,
  })

  // --- LOCAL STATE --- //

  const yandex_order = 'yandex_order'
  const gs_order = 'gs_order'
  const magnit_order = 'magnit_order'
  const consumer_order = 'consumer_order'
  const vkusvill_order = 'vkusvill_order'

  const [showMarketSelect, setShowMarketSelect] = React.useState(!market.selected)

  const [showProfile, setShowProfile] = useState(false)
  const [showOutlets, setShowOutlets] = useState(false)

  const [currentTime, setCurrentTime] = useState(new Date())
  const timeOptions = { hour12: false }

  // --- CALLBACKS --- //

  const closeMarketSelect = () => {
    setShowMarketSelect(false)
  }

  const openMarketSelect = () => {
    setShowMarketSelect(true)
  }

  const openOutlets = () => {
    setShowOutlets(true)
  }

  const closeOutlets = () => {
    setShowOutlets(false)
  }

  const openProfile = () => {
    setShowProfile(true)
  }

  const closeProfile = () => {
    setShowProfile(false)
  }

  const goToOrder = (orderId: number) => () => {
    navigate(`/order/${orderId}`)
  }

  // --- EFFECTS --- //

  React.useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000)

    return function cleanup() {
      clearInterval(timer)
    }
  })

  // --- RENDERING --- //

  const orders = ordersData?.orders ?? []

  return (
    <>
      <div className="page__container">
        <div className="page__content">
          {showMarketSelect ? <MarketSelect close={closeMarketSelect} /> : null}
          {showProfile ? <Profile close={closeProfile} /> : null}
          {showOutlets ? <OutletsList close={closeOutlets} /> : null}
          <div className="market">
            <div className="market__header">
              <div className="market__title">
                <div className="market__corners_list-icon" onClick={openOutlets}>
                  <svg width="26" height="26">
                    <use href={sprite + '#icon-shop'} />
                  </svg>
                </div>
                <div className="headerContainer">
                  <span onClick={openMarketSelect}>{selectedMarket ? selectedMarket.title : 'Не выбрано'}</span>
                  <svg onClick={openMarketSelect} width="14" height="8">
                    <use href={sprite + '#arrow-down'} />
                  </svg>
                </div>
                <div className="user__profile" onClick={openProfile}>
                  <svg width="24" height="24">
                    <use href={sprite + '#icon-login'} />
                  </svg>
                </div>
              </div>

              <div className="market__status_string">
                <div className="market__date">
                  {getDateString()} {currentTime.toLocaleTimeString('en-US', timeOptions)}
                </div>
                <div className="market__date"> {orders.length} заказов </div>
              </div>
            </div>
            <div className="market__items">
              {isLoading ? (
                <Loader />
              ) : isError ? (
                <div>Что-то пошло не так</div>
              ) : (
                orders.map((order) => (
                  <div
                    className={`market__item ${
                      order.eat_place === 'togo_yandex' ||
                      order.eat_place === 'togo_yandex_pickup' ||
                      order.eat_place === 'togo_sber'
                        ? yandex_order
                        : order.eat_place === 'consumer'
                        ? consumer_order
                        : order.eat_place === 'togo_magnit'
                        ? magnit_order
                        : order.eat_place === 'togo_vkusvill'
                        ? vkusvill_order
                        : gs_order
                    }`}
                    key={order.id}
                    onClick={goToOrder(order.id)}
                  >
                    <div className="market__col">
                      <div className="market__number">
                        {order.id}{' '}
                        {isRobotYandexDeliveryType(order.yandexDeliveryType) ? (
                          <RobotDeliveryIcon className="market__robot-icon" />
                        ) : null}{' '}
                        {order.eat_place === 'market_corner' || order.eat_place === 'market_table' ? (
                          !order.distanceFromMarketToCustomer ? (
                            <span className="distance_marker_danger">ПРОВЕРИТЬ!!!</span>
                          ) : order.distanceFromMarketToCustomer < 0.2 ? (
                            <span className="distance_marker_norm">
                              ✅{/* ({order.distanceFromMarketToCustomer} км.) */}
                            </span>
                          ) : order.distanceFromMarketToCustomer >= 0.2 ? (
                            <span className="distance_marker_danger">
                              {' '}
                              ПРОВЕРИТЬ!!!
                              {/* ({order.distanceFromMarketToCustomer.toPrecision(5)} км) */}
                            </span>
                          ) : null
                        ) : null}
                      </div>
                      <div className="order__eat_place">
                        {order.address !== 'нет адреса' && order.eat_place === 'togo_yandex' ? <span>🛵 </span> : null}
                        {parseEatPlace(order.eat_place, order.car_number, order.table)}
                      </div>
                      {order.eat_place === 'togo_yandex' ||
                      order.eat_place === 'togo_yandex_pickup' ||
                      order.eat_place === 'togo_sber' ||
                      order.eat_place === 'consumer' ||
                      order.eat_place === 'togo_magnit' ||
                      order.eat_place === 'togo_vkusvill' ? (
                        <div className="order__eat_place">{order.eats_id}</div>
                      ) : null}
                      <div className="market__sum">{order.full_cost} ₽</div>
                    </div>

                    <div className="market__col market__col_right">
                      <div className="corner__list">
                        {order.order_outlets.map(({ id, title, status }) => (
                          <div className="market__row" key={id}>
                            <div className="market__name">{title}</div>
                            <div className={`market__status market__status_${status}`}>
                              {translateToStyleCorner(status)}
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="order__status-time">
                        <div className={`order__status-small order__status-${order.ready_status}`}>
                          {translateToStyleOrder(order.ready_status)}
                        </div>
                        <div className="order__time">{getTimeString(order.created_at)}</div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default Market
